import { useEffect, useRef, useState } from "react";
import { CheckCircle2, Circle, Loader2, XCircle, Database } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Progress } from "@/components/ui/progress";
import { SCHEMA } from "@/constants/testIds";
import api from "@/lib/api";

const STAGE_ICON = {
  pending: <Circle className="h-4 w-4 text-muted-foreground/50" />,
  running: <Loader2 className="h-4 w-4 animate-spin text-accent" />,
  completed: <CheckCircle2 className="h-4 w-4 text-accent" />,
  failed: <XCircle className="h-4 w-4 text-destructive" />,
};

function StageRow({ stage }) {
  const status = stage.status || "pending";
  const showCount = stage.total > 0 && (status === "running" || status === "completed");
  return (
    <li
      data-testid={`${SCHEMA.connStage}-${stage.key}`}
      data-status={status}
      className="flex items-start gap-3 rounded-lg border border-border/50 bg-card/40 px-3 py-3"
    >
      <span className="mt-0.5 shrink-0">{STAGE_ICON[status] || STAGE_ICON.pending}</span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span
            className={`text-sm font-medium ${
              status === "pending" ? "text-muted-foreground" : "text-foreground"
            }`}
          >
            {stage.label}
          </span>
          {showCount && (
            <span data-testid={SCHEMA.connDone} className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
              {stage.done} / {stage.total}
            </span>
          )}
        </div>
        {stage.message && (
          <p className="mt-0.5 truncate text-xs text-muted-foreground">{stage.message}</p>
        )}
        {status === "running" && stage.total > 0 && (
          <Progress value={stage.stage_progress || 0} className="mt-2 h-1" />
        )}
      </div>
    </li>
  );
}

export function ConnectionProgressDrawer({ jobId, open, onOpenChange, onDone, title = "Connecting data source" }) {
  const [job, setJob] = useState(null);
  const doneRef = useRef(false);

  useEffect(() => {
    if (!open || !jobId) return;
    doneRef.current = false;
    setJob(null);
    let active = true;
    let timer;

    const poll = async () => {
      try {
        const { data } = await api.get(`/jobs/${jobId}`);
        if (!active) return;
        setJob(data);
        if ((data.status === "completed" || data.status === "failed") && !doneRef.current) {
          doneRef.current = true;
          onDone?.(data);
          return; // stop polling
        }
      } catch {
        /* transient — keep polling */
      }
      if (active) timer = setTimeout(poll, 1500);
    };
    poll();

    return () => {
      active = false;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, jobId]);

  const stages = job?.stages || [];
  const overall = job?.progress ?? 0;
  const failed = job?.status === "failed";
  const completed = job?.status === "completed";
  const running = !failed && !completed;

  return (
    <Sheet open={open} onOpenChange={(o) => (running ? null : onOpenChange?.(o))}>
      <SheetContent
        side="right"
        data-testid={SCHEMA.connDrawer}
        className="w-full font-mono sm:max-w-md"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 text-lg">
            <Database className="h-5 w-5 text-accent" />
            {title}
          </SheetTitle>
          <SheetDescription className="text-xs">
            {failed
              ? "Something went wrong. Review the failed step below and try again."
              : completed
                ? "All steps complete — your semantic layer is ready."
                : "Each step runs transparently. Large schemas are processed in parallel."}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6" data-testid={SCHEMA.connOverall} data-progress={overall}>
          <div className="mb-2 flex items-center justify-between text-xs">
            <span className="uppercase tracking-[0.16em] text-muted-foreground">
              {completed ? "Done" : failed ? "Failed" : "Overall"}
            </span>
            <span className="tabular-nums font-semibold">{overall}%</span>
          </div>
          <Progress
            value={overall}
            className={`h-2 ${failed ? "[&>div]:bg-destructive" : ""}`}
          />
        </div>

        <ul className="mt-6 space-y-2">
          {(stages.length
            ? stages
            : [
                { key: "connect", label: "Connect database", status: "running" },
                { key: "schema", label: "Load schema", status: "pending" },
                { key: "relationships", label: "Infer relationships", status: "pending" },
                { key: "glossary", label: "Generate glossary", status: "pending" },
                { key: "embeddings", label: "Build embeddings", status: "pending" },
              ]
          ).map((s) => (
            <StageRow key={s.key} stage={s} />
          ))}
        </ul>
      </SheetContent>
    </Sheet>
  );
}
